import { ReactNode, useEffect, useMemo } from "react";
import { renderToString } from "react-dom/server";
import { ColumnDef, flexRender, GroupColumnDef, HeaderContext } from "@tanstack/react-table";
import { Column } from "@tanstack/table-core";
import { useTableContext } from "../context";
import { AccessorPathColumnDef, PrettifyFilterValueSignature, TableColumnDef } from "../types";
import { defaultPrettifyFilterValue } from "./filters";

import { ReactComponent as Chevron } from "./icons/chevron-right.svg";

import styles from "./styles.module.scss";

export function setupColumns<T>(columns: TableColumnDef<T>[]): ColumnDef<T>[] {
  return columns.map(c => {
    c.meta = c.meta || {};
    c.meta.prettifyFilterValue = c.meta.prettifyFilterValue ?? defaultPrettifyFilterValue as PrettifyFilterValueSignature<T>;
    c.filterFn = c.filterFn ?? c.meta.defaultFilterFn ?? "equals";
    c.meta.defaultFilterFn = c.meta.defaultFilterFn ?? c.filterFn;
    c.meta.additionalFilterFn = c.meta.additionalFilterFn ?? [];
    c.meta.availableInSettings = c.meta.availableInSettings ?? true;

    const withAccessorPath = c as AccessorPathColumnDef<T>;
    if (withAccessorPath.accessorPath) {
      const { accessorPath, ...props } = withAccessorPath;
      return {
        ...props,
        accessorPath,
        accessorKey: accessorPath?.toString(),
      };
    }
    const grouped = c as GroupColumnDef<T>;
    if (grouped.columns) {
      const { columns, ...props } = grouped;
      return {
        ...props,
        columns: setupColumns(columns),
      };
    }
    return c as ColumnDef<T>;
  });
}

export function useSetupColumns<T>(columns: TableColumnDef<T>[]) {
  return useMemo(() => setupColumns(columns), [ columns ]);
}

const innerRenderColumnHeader = (column: Column<Object, unknown>, context: HeaderContext<Object, unknown> = {
  column,
  header: {} as any,
  table: {} as any,
}): ReactNode => {
  const element = flexRender(column.columnDef.header, context);
  return (
    <>
      {
        column.parent &&
        <>
          {innerRenderColumnHeader(column.parent)}
          <Chevron data-arrow="true" />
        </>
      }
      {typeof element === "string" ? <span>{element}</span> : element}
    </>
  );
};

export const renderColumnHeader = (column: Column<Object, unknown>, context: HeaderContext<Object, unknown> = {
  column,
  header: {} as any,
  table: {} as any,
}): ReactNode => (
  <div className={styles.root}>
    {innerRenderColumnHeader(column, context)}
  </div>
);

export const renderColumnHeaderAsText = (column: Column<Object, unknown>, context: HeaderContext<Object, unknown> = {
  column,
  header: {} as any,
  table: {} as any,
}): string => (
  renderToString(renderColumnHeader(column, context) as any)
    .replace(/<[^>]*>/igm, " ")
    .replaceAll("  ", " ")
    .replaceAll("  ", " ")
);

export function useColumnSizeScaling(tableWidth: number, desiredWidth: number) {
  const table = useTableContext();

  useEffect(() => {
    if (tableWidth < desiredWidth) {
      const columns = table.getLeafHeaders()
        .filter(x => !x.subHeaders.length)
        .map(x => x.column)
        .filter(x => !!x.columnDef.meta?.autoscaling);

      const totalParts = columns.reduce((acc, v) => acc + Number(v.columnDef.meta?.autoscaling), 0);

      table.setColumnSizing(old => {
        const newState = { ...old };

        columns.forEach(c => {
          newState[c.id] = c.getSize() + ((desiredWidth - tableWidth) / totalParts) * Number(c.columnDef.meta?.autoscaling);
        });

        return newState;
      });
    }
  }, [ tableWidth, desiredWidth, table ]);
}